import React from "react";

import { Element as DomElement } from "html-react-parser";

// 실제로 렌더링/실행되면 곤란한 태그들
const RAW_TAG_NAMES = new Set([
  "input",
  "textarea",
  "select",
  "option",
  "button",
  "script",
  "iframe",
  "form",
]);

/**
 * 폼/스크립트/임베드 계열 태그인지 여부를 판별
 */
export function isRawTag(element: DomElement): boolean {
  return RAW_TAG_NAMES.has(element.name);
}

/**
 * 폼/스크립트/임베드 계열 태그를
 * 실제 동작 없이 태그 문자열로만 렌더링하는 helper
 *
 * 예: <input name="noit" /> → "<input name="noit" />"
 */
export function renderRawTag(element: DomElement): React.ReactElement {
  const attribs = ((element as any).attribs || {}) as Record<string, unknown>;

  // dpe-e2e emits a detected checkbox as `<input type="checkbox"[ checked]>` (wrapped in a
  // <label>, which renders normally). Surface it as a real, non-interactive checkbox so the
  // model's checkbox detection is actually visible in the preview. Every other form/script/
  // embed tag still falls through to the inert-text guard below.
  if (
    element.name === "input" &&
    String(attribs.type).toLowerCase() === "checkbox"
  ) {
    // Non-interactive but high-contrast: `pointer-events-none` blocks toggling and `readOnly`
    // suppresses React's controlled-without-onChange warning, while `accent-current` keeps the
    // checked mark at text contrast (a `disabled` box greys out and can read as empty in some
    // themes — the opposite of what this fix is for).
    return (
      <input
        type="checkbox"
        checked={"checked" in attribs}
        readOnly
        className="align-middle pointer-events-none accent-current"
      />
    );
  }

  const attrsString = Object.entries(attribs)
    .map(([key, val]) => `${key}="${String(val)}"`)
    .join(" ");

  const isVoid = element.name === "input";
  const tagText = isVoid
    ? attrsString
      ? `<${element.name} ${attrsString} />`
      : `<${element.name} />`
    : attrsString
      ? `<${element.name} ${attrsString}>...</${element.name}>`
      : `<${element.name}>...</${element.name}>`;

  // React가 문자열을 그대로 escape 해주므로, 실제 DOM에는 텍스트로만 표시된다.
  return (
    <span className="flex whitespace-pre-wrap text-muted-foreground">
      {tagText}
    </span>
  );
}
